(function (window, document) {
  'use strict';

  var APP_NAMESPACE = 'StorefrontTracking';
  var LEGACY_NAMESPACE = 'ProtoArcTracking';
  var existingTracking = window[APP_NAMESPACE] || window[LEGACY_NAMESPACE];

  if (existingTracking && existingTracking.version) {
    window[APP_NAMESPACE] = existingTracking;
    window[LEGACY_NAMESPACE] = existingTracking;
    return;
  }

  var config = window.__TRACKING_CONFIG__ || {};
  var context = window.__TRACKING_CONTEXT__ || {};
  var featureFlags = config.feature_flags || {};
  var eventCatalog = config.event_catalog || {};
  var state = {
    gtagPromise: null,
    gtagConfigured: false,
    onceKeys: {}
  };

  function log() {
    if (!featureFlags.debug || !window.console || !console.log) return;
    console.log.apply(console, ['[StorefrontTracking]'].concat(Array.prototype.slice.call(arguments)));
  }

  function warn() {
    if (!window.console || !console.warn) return;
    console.warn.apply(console, ['[StorefrontTracking]'].concat(Array.prototype.slice.call(arguments)));
  }

  function cleanObject(source) {
    var output = {};

    Object.keys(source || {}).forEach(function (key) {
      var value = source[key];

      if (value === null || typeof value === 'undefined') return;
      if (Array.isArray(value) && value.length === 0) return;
      output[key] = value;
    });

    return output;
  }

  function normalizeNumber(value) {
    if (typeof value === 'number' && !isNaN(value)) return value;
    if (typeof value === 'string' && value.trim() !== '') {
      var parsed = Number(value);
      if (!isNaN(parsed)) return parsed;
    }
    return null;
  }

  function normalizeItems(items) {
    return Array.isArray(items) ? items : [];
  }

  function generateEventId(name) {
    return [
      name,
      Date.now(),
      Math.random().toString(36).slice(2, 10)
    ].join('_');
  }

  function getConsentState() {
    var consent = Object.assign({
      analytics: true,
      marketing: true
    }, config.consent || {});

    try {
      var privacy = window.Shopify && window.Shopify.customerPrivacy;

      if (privacy && typeof privacy.analyticsProcessingAllowed === 'function') {
        consent.analytics = privacy.analyticsProcessingAllowed();
      }

      if (privacy && typeof privacy.marketingAllowed === 'function') {
        consent.marketing = privacy.marketingAllowed();
      }
    } catch (error) {
      log('Customer privacy API unavailable, using config defaults.', error);
    }

    return consent;
  }

  function setGtagConsent() {
    var consent = getConsentState();

    if (typeof window.gtag !== 'function') return;

    window.gtag('consent', 'default', {
      analytics_storage: consent.analytics ? 'granted' : 'denied',
      ad_storage: consent.marketing ? 'granted' : 'denied',
      ad_user_data: consent.marketing ? 'granted' : 'denied',
      ad_personalization: consent.marketing ? 'granted' : 'denied'
    });
  }

  function ensureGtag() {
    var ga4Id = config.ga4 && config.ga4.measurement_id;
    var adsId = config.google_ads && config.google_ads.aw_id;
    var primaryId = ga4Id || adsId;

    if (!primaryId) {
      return Promise.resolve(false);
    }

    if (state.gtagPromise) {
      return state.gtagPromise;
    }

    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function () {
      window.dataLayer.push(arguments);
    };

    setGtagConsent();

    state.gtagPromise = new Promise(function (resolve, reject) {
      var existing = document.querySelector('script[src*="googletagmanager.com/gtag/js"]');

      if (existing) {
        resolve();
        return;
      }

      var script = document.createElement('script');
      script.async = true;
      script.src = 'https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(primaryId);
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    }).then(function () {
      if (!state.gtagConfigured) {
        var userId = context.customer_id || (context.customer && context.customer.id) || null;

        window.gtag('js', new Date());
        setGtagConsent();

        if (ga4Id) {
          window.gtag('config', ga4Id, cleanObject({
            send_page_view: false,
            debug_mode: !!featureFlags.debug,
            user_id: userId
          }));
        }

        if (adsId) {
          window.gtag('config', adsId, cleanObject({
            allow_enhanced_conversions: true,
            user_id: userId
          }));
        }

        if (userId) {
          window.gtag('set', 'user_properties', {
            customer_id: String(userId)
          });
        }

        state.gtagConfigured = true;
      }

      return true;
    }).catch(function (error) {
      warn('Failed to load gtag.js.', error);
      return false;
    });

    return state.gtagPromise;
  }

  function shouldAttachItems(name) {
    return [
      'view_item',
      'view_item_list',
      'select_item',
      'add_to_cart',
      'view_cart',
      'begin_checkout',
      'purchase'
    ].indexOf(name) !== -1;
  }

  function buildEventPayload(name, payload) {
    var page = context.page || {};
    var ecommerce = context.ecommerce || {};
    var cart = context.cart || {};
    var merged = Object.assign({
      event_id: generateEventId(name),
      event_time: new Date().toISOString(),
      brand: config.brand || context.brand || null,
      page_type: context.page_type || page.type || null,
      template_name: context.template_name || page.template_name || null,
      page_path: page.path || window.location.pathname,
      page_title: page.title || document.title || null,
      currency: ecommerce.currency || context.currency || (context.shop && context.shop.currency) || null,
      customer_status: context.customer_status || (context.customer && context.customer.status) || 'guest'
    }, payload || {});

    if (typeof merged.customer_id === 'undefined' && typeof context.customer_id !== 'undefined') {
      merged.customer_id = context.customer_id;
    }

    if (typeof merged.value === 'undefined' || merged.value === null) {
      if (name === 'view_cart' && typeof cart.total_value !== 'undefined') {
        merged.value = cart.total_value;
      } else if (typeof ecommerce.value !== 'undefined') {
        merged.value = ecommerce.value;
      }
    }

    if (shouldAttachItems(name) && normalizeItems(merged.items).length === 0) {
      if (name === 'view_cart' && normalizeItems(cart.items).length) {
        merged.items = cart.items;
      } else {
        merged.items = ecommerce.items;
      }
    }

    if (name === 'search' && typeof merged.search_term === 'undefined') {
      merged.search_term = ecommerce.search_term;
    }

    if (name === 'view_item_list') {
      if (typeof merged.item_list_id === 'undefined') merged.item_list_id = ecommerce.item_list_id;
      if (typeof merged.item_list_name === 'undefined') merged.item_list_name = ecommerce.item_list_name;
      if (typeof merged.collection_id === 'undefined') merged.collection_id = ecommerce.collection_id;
      if (typeof merged.collection_name === 'undefined') merged.collection_name = ecommerce.collection_name;
    }

    if (shouldAttachItems(name)) {
      merged.items = normalizeItems(merged.items);
    }

    if (merged.value !== null && typeof merged.value !== 'undefined') {
      var numericValue = normalizeNumber(merged.value);
      if (numericValue !== null) {
        merged.value = numericValue;
      }
    }

    return merged;
  }

  function buildGa4Params(name, payload) {
    return cleanObject({
      event_id: payload.event_id,
      page_type: payload.page_type,
      template_name: payload.template_name,
      page_path: payload.page_path,
      page_title: payload.page_title,
      currency: payload.currency,
      value: normalizeNumber(payload.value),
      items: normalizeItems(payload.items),
      item_list_id: payload.item_list_id,
      item_list_name: payload.item_list_name,
      collection_id: payload.collection_id,
      collection_name: payload.collection_name,
      search_term: payload.search_term,
      customer_status: payload.customer_status,
      resource: payload.resource,
      resource_id: payload.resource_id,
      slot: payload.slot,
      delta: payload.delta
    });
  }

  function dispatchGa4(name, payload) {
    if (!featureFlags.ga4_enabled) {
      return Promise.resolve({ destination: 'ga4', status: 'skipped', reason: 'disabled' });
    }

    if (!getConsentState().analytics) {
      return Promise.resolve({ destination: 'ga4', status: 'skipped', reason: 'analytics_consent_denied' });
    }

    return ensureGtag().then(function (ready) {
      if (!ready || typeof window.gtag !== 'function') {
        return { destination: 'ga4', status: 'skipped', reason: 'gtag_unavailable' };
      }

      var params = buildGa4Params(name, payload);
      window.gtag('event', name, params);
      log('GA4 event sent', name, params);

      return { destination: 'ga4', status: 'sent', data: params };
    });
  }

  function buildAdsParams(payload, sendTo) {
    return cleanObject({
      send_to: sendTo,
      value: normalizeNumber(payload.value),
      currency: payload.currency,
      transaction_id: payload.transaction_id
    });
  }

  function dispatchGoogleAds(name, payload) {
    var adsConfig = config.google_ads || {};
    var conversionConfig = adsConfig.conversions && adsConfig.conversions[name];
    var sendTo = conversionConfig && conversionConfig.send_to;

    if (!featureFlags.google_ads_enabled) {
      return Promise.resolve({ destination: 'google_ads', status: 'skipped', reason: 'disabled' });
    }

    if (!sendTo) {
      return Promise.resolve({ destination: 'google_ads', status: 'skipped', reason: 'missing_mapping' });
    }

    if (!getConsentState().marketing) {
      return Promise.resolve({ destination: 'google_ads', status: 'skipped', reason: 'marketing_consent_denied' });
    }

    return ensureGtag().then(function (ready) {
      if (!ready || typeof window.gtag !== 'function') {
        return { destination: 'google_ads', status: 'skipped', reason: 'gtag_unavailable' };
      }

      if (adsConfig.enhanced_conversions && payload.user_data) {
        window.gtag('set', 'user_data', payload.user_data);
      }

      var params = buildAdsParams(payload, sendTo);
      window.gtag('event', 'conversion', params);
      log('Google Ads conversion sent', name, params);

      return { destination: 'google_ads', status: 'sent', data: params };
    });
  }

  function dispatchFirstParty(name, payload) {
    var likesConfig = config.first_party && config.first_party.likes;

    if (!featureFlags.first_party_enabled) {
      return Promise.resolve({ destination: 'first_party', status: 'skipped', reason: 'disabled' });
    }

    if (name !== 'like_click') {
      return Promise.resolve({ destination: 'first_party', status: 'skipped', reason: 'unsupported_event' });
    }

    if (!likesConfig || !likesConfig.endpoint) {
      return Promise.resolve({ destination: 'first_party', status: 'skipped', reason: 'missing_endpoint' });
    }

    if (likesConfig.require_login && !context.customer_id && !payload.customer_id) {
      return Promise.resolve({ destination: 'first_party', status: 'skipped', reason: 'login_required' });
    }

    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timeoutId = null;
    var headers = {
      'Content-Type': 'application/json'
    };

    if (likesConfig.open_token) {
      headers['X-Worker-Open-Token'] = likesConfig.open_token;
    }

    if (controller) {
      timeoutId = window.setTimeout(function () {
        controller.abort();
      }, likesConfig.timeout_ms || 15000);
    }

    return fetch(likesConfig.endpoint, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(cleanObject({
        brand: config.brand,
        resource: payload.resource || 'page',
        resource_id: payload.resource_id || 'unknown',
        event: payload.event || name,
        slot: payload.slot || 'default',
        delta: normalizeNumber(payload.delta) || 1,
        customer_id: payload.customer_id || context.customer_id,
        page_path: payload.page_path
      })),
      signal: controller ? controller.signal : undefined
    }).then(function (response) {
      if (timeoutId) window.clearTimeout(timeoutId);

      return response.text().then(function (rawText) {
        var data = {};

        try {
          data = rawText ? JSON.parse(rawText) : {};
        } catch (error) {
          data = { status: 'error', error: 'invalid_json', raw: rawText };
        }

        if (!response.ok) {
          return {
            destination: 'first_party',
            status: 'error',
            error: data.error || rawText || ('HTTP ' + response.status),
            data: data
          };
        }

        return {
          destination: 'first_party',
          status: 'sent',
          data: data
        };
      });
    }).catch(function (error) {
      if (timeoutId) window.clearTimeout(timeoutId);

      return {
        destination: 'first_party',
        status: 'error',
        error: error && error.name === 'AbortError' ? 'timeout' : (error && error.message) || 'request_failed'
      };
    });
  }

  function resolveDestinations(name, options) {
    if (options && Array.isArray(options.destinations) && options.destinations.length) {
      return options.destinations;
    }

    return (eventCatalog[name] && eventCatalog[name].destinations) || [];
  }

  function isDuplicate(name, payload, options) {
    var onceKey = options && options.onceKey;

    if (!onceKey && eventCatalog[name] && eventCatalog[name].once_per_page) {
      onceKey = name + ':' + (payload.page_path || window.location.pathname);
    }

    if (!onceKey) return false;
    if (state.onceKeys[onceKey]) return true;

    state.onceKeys[onceKey] = true;
    return false;
  }

  function dispatch(name, payload, destinations) {
    var destinationResults = {};
    var tasks = destinations.map(function (destination) {
      var dispatcher;

      if (destination === 'ga4') dispatcher = dispatchGa4;
      if (destination === 'google_ads') dispatcher = dispatchGoogleAds;
      if (destination === 'first_party') dispatcher = dispatchFirstParty;

      if (!dispatcher) {
        destinationResults[destination] = { destination: destination, status: 'skipped', reason: 'unknown_destination' };
        return Promise.resolve();
      }

      return dispatcher(name, payload).then(function (result) {
        destinationResults[destination] = result;
      });
    });

    return Promise.all(tasks).then(function () {
      return destinationResults;
    });
  }

  function emitTrackingEvent(name, payload, destinations) {
    document.dispatchEvent(new CustomEvent('tracking:sent', {
      detail: {
        name: name,
        payload: payload,
        destinations: destinations
      }
    }));
  }

  function track(name, payload, options) {
    var normalizedPayload = buildEventPayload(name, payload);
    var destinations = resolveDestinations(name, options || {});

    if (isDuplicate(name, normalizedPayload, options || {})) {
      return Promise.resolve({
        event: name,
        payload: normalizedPayload,
        status: 'skipped',
        reason: 'duplicate',
        destinations: {}
      });
    }

    if (!destinations.length) {
      return Promise.resolve({
        event: name,
        payload: normalizedPayload,
        status: 'skipped',
        reason: 'no_destinations',
        destinations: {}
      });
    }

    return dispatch(name, normalizedPayload, destinations).then(function (destinationResults) {
      emitTrackingEvent(name, normalizedPayload, destinationResults);

      return {
        event: name,
        payload: normalizedPayload,
        status: 'sent',
        destinations: destinationResults
      };
    });
  }

  function trackAutoPageEvents() {
    if (!featureFlags.auto_page_events) return;

    track('page_view', {}, { onceKey: 'page_view:' + (context.page && context.page.path || window.location.pathname) });

    if (context.page_type === 'product' && normalizeItems(context.ecommerce && context.ecommerce.items).length) {
      track('view_item');
      return;
    }

    if (context.page_type === 'collection' && normalizeItems(context.ecommerce && context.ecommerce.items).length) {
      track('view_item_list');
      return;
    }

    if (context.page_type === 'search' && context.ecommerce && context.ecommerce.search_term) {
      track('search', {
        search_term: context.ecommerce.search_term,
        value: context.ecommerce.value,
        items: context.ecommerce.items
      });
      return;
    }

    if (context.page_type === 'cart') {
      track('view_cart', {
        value: context.cart && context.cart.total_value,
        items: context.cart && context.cart.items
      });
    }
  }

  function bindCustomEvents() {
    document.addEventListener('tracking:event', function (event) {
      var detail = event.detail || {};

      if (!detail.name) return;
      track(detail.name, detail.payload || {}, detail.options || {});
    });

    [
      'select_item',
      'add_to_cart',
      'begin_checkout',
      'purchase',
      'generate_lead'
    ].forEach(function (eventName) {
      document.addEventListener('tracking:' + eventName, function (event) {
        track(eventName, (event && event.detail) || {});
      });
    });
  }

  function buildItemFromDataset(el) {
    if (!el || !el.dataset || !el.dataset.trackItemId) return [];

    return [cleanObject({
      item_id: el.dataset.trackItemId,
      product_id: el.dataset.trackProductId,
      sku: el.dataset.trackSku,
      item_name: el.dataset.trackItemName,
      item_brand: el.dataset.trackItemBrand,
      item_category: el.dataset.trackItemCategory,
      item_variant: el.dataset.trackItemVariant,
      price: normalizeNumber(el.dataset.trackPrice),
      quantity: normalizeNumber(el.dataset.trackQuantity) || 1
    })];
  }

  function bindDeclarativeElements() {
    document.addEventListener('click', function (event) {
      var el = event.target.closest('[data-track-event]');

      if (!el) return;

      var eventName = el.dataset.trackEvent;
      if (!eventName) return;

      track(eventName, cleanObject({
        resource: el.dataset.resource,
        resource_id: el.dataset.resourceId,
        slot: el.dataset.slot,
        value: normalizeNumber(el.dataset.trackValue),
        item_list_id: el.dataset.itemListId,
        item_list_name: el.dataset.itemListName,
        search_term: el.dataset.searchTerm,
        items: buildItemFromDataset(el)
      }));
    });

    document.addEventListener('click', function (event) {
      var el = event.target.closest('button[name="checkout"], input[name="checkout"], a[href*="/checkout"], [data-track-begin-checkout]');

      if (!el) return;

      track('begin_checkout', {
        value: context.cart && context.cart.total_value,
        items: context.cart && context.cart.items
      });
    });
  }

  function init() {
    bindCustomEvents();
    bindDeclarativeElements();
    ensureGtag();
    trackAutoPageEvents();
  }

  var api = {
    version: '1.0.0',
    config: config,
    context: context,
    getConsentState: getConsentState,
    track: track,
    trackLeadSuccess: function (payload) {
      return track('generate_lead', payload || {});
    },
    trackAddToCartSuccess: function (payload) {
      return track('add_to_cart', payload || {});
    },
    trackPurchase: function (payload) {
      return track('purchase', payload || {});
    },
    trackBeginCheckout: function (payload) {
      return track('begin_checkout', payload || {});
    }
  };

  window[APP_NAMESPACE] = api;
  window[LEGACY_NAMESPACE] = api;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})(window, document);
